// Sound for Tiny Town: every effect is synthesized with WebAudio (no files to download), and the town music is
// a small sequencer playing a cozy loop. Built for crowds:
//   - at most MAX_VOICES effects at once; the quietest new sound is dropped when full
//   - each kind of sound from other players is throttled, so 200 people dash-spamming is a soft patter, not noise
//   - other players' sounds fade with distance and pan left/right
// Phones only allow sound after a tap or key press, so the engine starts on the first one. Everything pauses
// while the tab is hidden.

export type Sfx = 'dash' | 'attack' | 'loot' | 'level' | 'click' | 'open' | 'close' | 'invite' | 'join' | 'ping' | 'error' | 'blip' | 'whisper' | 'shutter' | 'hit' | 'hurt' | 'poof' | 'dodge' | 'jump' | 'mount' | 'battle' | 'chain'
  | 'fire' | 'ice' | 'thunder' | 'holy' | 'heal' | 'buff' | 'debuff' | 'steal' | 'burst' | 'poison' | 'cast' | 'fanfare';
export interface Volumes { master: number; music: number; sfx: number; ui: number; others: boolean }

const MAX_VOICES = 14;
/** The mix: the music sits clearly over the rain, and after dark (or in the rain) it only softens a little. */
const MUSIC_LEVEL = 0.85, RAIN_LEVEL = 0.06, MELLOW_HZ = 3600;
/** Minimum gap between two of the same sound coming from other players (ms). */
const OTHERS_GAP: Partial<Record<Sfx, number>> = { dash: 70, attack: 90, blip: 40, hit: 60, poof: 80 };

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master!: GainNode; private musicGain!: GainNode; private sfxGain!: GainNode; private uiGain!: GainNode;
  private musicTone!: BiquadFilterNode; private rainGain: GainNode | null = null; private cricketT = 0;
  private amb = { rain: 0, night: false };
  private noise: AudioBuffer | null = null;
  private voices = 0;
  private lastOthers = new Map<Sfx, number>();
  private music: Music | null = null;
  vol: Volumes = { master: 0.8, music: 0.5, sfx: 0.8, ui: 0.6, others: true };
  /** Effects playing right now (shown in the developer panel). */
  get playing(): number { return this.voices; }
  dropped = 0;

  constructor() {
    const unlock = () => { this.start(); };
    addEventListener('pointerdown', unlock, { capture: true });
    addEventListener('keydown', unlock, { capture: true });
    document.addEventListener('visibilitychange', () => {
      if (!this.ctx) return;
      if (document.hidden) void this.ctx.suspend(); else void this.ctx.resume();
    });
  }

  /** Create or resume the audio context. Safe to call often; only works inside a tap or key press the first time. */
  start(): void {
    if (this.ctx) { if (this.ctx.state === 'suspended' && !document.hidden) void this.ctx.resume(); return; }
    const AC = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return;
    const ctx = new AC({ latencyHint: 'interactive' });
    this.ctx = ctx;
    this.master = ctx.createGain(); this.master.connect(ctx.destination);
    // a gentle limiter, so many sounds at once never clip
    const comp = ctx.createDynamicsCompressor(); comp.threshold.value = -14; comp.ratio.value = 6; comp.attack.value = 0.003; comp.release.value = 0.15;
    comp.connect(this.master);
    this.musicTone = ctx.createBiquadFilter(); this.musicTone.type = 'lowpass'; this.musicTone.frequency.value = 18000; this.musicTone.connect(comp);
    this.musicGain = ctx.createGain(); this.musicGain.connect(this.musicTone);
    this.sfxGain = ctx.createGain(); this.sfxGain.connect(comp);
    this.uiGain = ctx.createGain(); this.uiGain.connect(comp);
    const n = ctx.createBuffer(1, ctx.sampleRate * 0.5, ctx.sampleRate), d = n.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    this.noise = n;
    this.applyVolumes();
    this.music = new Music(ctx, this.musicGain); this.music.setSong(this.wantSong);
    if (this.wantMusic) this.music.play();
  }

  setVolumes(v: Volumes): void { this.vol = { ...v }; this.applyVolumes(); }
  private applyVolumes(): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime, set = (g: GainNode, v: number) => g.gain.setTargetAtTime(v * v, t, 0.03);   // squared: sliders feel even
    set(this.master, this.vol.master); if (!this.hushed) set(this.musicGain, this.vol.music * MUSIC_LEVEL); set(this.sfxGain, this.vol.sfx); set(this.uiGain, this.vol.ui);
  }

  private wantMusic = false; private wantSong: SongId = 'town'; private hushed = false;
  /** Switch song (a fight starts, a fight ends), or null: let the music fade out (the victory jingle plays alone). */
  song(id: SongId | null): void {
    const t = this.ctx?.currentTime ?? 0;
    if (id === null) { this.hushed = true; if (this.ctx) this.musicGain.gain.setTargetAtTime(0, t, 0.12); return; }
    this.wantSong = id; this.music?.setSong(id);
    if (this.hushed) { this.hushed = false; if (this.ctx) this.musicGain.gain.setTargetAtTime((this.vol.music * MUSIC_LEVEL) ** 2, t + 0.15, 0.4); }
    if (this.ctx) this.musicTone.frequency.setTargetAtTime(id !== 'town' ? 18000 : this.mellow ? MELLOW_HZ : 18000, t, 0.2);
  }
  private mellow = false;
  playMusic(on: boolean): void { this.wantMusic = on; if (!this.music) return; if (on) this.music.play(); else this.music.stop(); }

  /** Weather and time of day: a rain bed, crickets at night, and softer music after dark or in the rain. */
  setAmbience(rain: number, night: boolean): void {
    this.amb = { rain, night };
    const ctx = this.ctx; if (!ctx || ctx.state !== 'running') return;
    const t = ctx.currentTime;
    this.mellow = night || rain > 0.3;
    if (this.wantSong === 'town') this.musicTone.frequency.setTargetAtTime(this.mellow ? MELLOW_HZ : 18000, t, 1.5);
    if (rain > 0.01 && !this.rainGain) {
      const src = ctx.createBufferSource(), f = ctx.createBiquadFilter(), f2 = ctx.createBiquadFilter(), g = ctx.createGain();
      src.buffer = this.noise; src.loop = true; f.type = 'lowpass'; f.frequency.value = 1500; f2.type = 'highpass'; f2.frequency.value = 300; g.gain.value = 0;
      src.connect(f); f.connect(f2); f2.connect(g); g.connect(this.sfxGain); src.start();
      this.rainGain = g;
    }
    this.rainGain?.gain.setTargetAtTime(rain * RAIN_LEVEL, t, 1);
    const now = performance.now();
    if (night && rain < 0.3 && now > this.cricketT) {
      this.cricketT = now + 300 + Math.random() * 1600;
      const n = 2 + Math.floor(Math.random() * 3), f = 4200 + Math.random() * 900, pan = ctx.createStereoPanner(); pan.pan.value = Math.random() * 1.6 - 0.8; pan.connect(this.sfxGain);
      for (let i = 0; i < n; i++) tone(ctx, pan, 'sine', f, f, t + i * 0.07, 0.04, 0.012, 0.004);
      setTimeout(() => pan.disconnect(), 1000);
    }
  }

  /** A roll of thunder, a moment after the flash. */
  thunder(): void {
    const ctx = this.ctx; if (!ctx || ctx.state !== 'running' || !this.noise) return;
    const t = ctx.currentTime, s = ctx.createBufferSource(), f = ctx.createBiquadFilter(), g = ctx.createGain();
    s.buffer = this.noise; s.loop = true; f.type = 'lowpass'; f.frequency.setValueAtTime(320, t); f.frequency.exponentialRampToValueAtTime(90, t + 2.6);
    g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(0.5, t + 0.08); g.gain.exponentialRampToValueAtTime(0.0001, t + 2.8);
    s.connect(f); f.connect(g); g.connect(this.sfxGain); s.start(t); s.stop(t + 3);
  }

  /** Play an effect. `at` is the source's offset from the listener in pixels, for other players' sounds. */
  play(s: Sfx, at?: { dx: number; dy: number }, pitch = 1): void {
    const ctx = this.ctx; if (!ctx || ctx.state !== 'running') return;
    let gain = 1, pan = 0;
    if (at) {
      if (!this.vol.others) return;
      const gap = OTHERS_GAP[s] ?? 60, now = performance.now();
      if (now - (this.lastOthers.get(s) ?? 0) < gap) { this.dropped++; return; }
      this.lastOthers.set(s, now);
      const d = Math.hypot(at.dx, at.dy); if (d > 220) return;
      gain = 0.55 * Math.max(0, 1 - d / 220); pan = Math.max(-0.8, Math.min(0.8, at.dx / 160));
      if (gain < 0.04) return;
    }
    if (this.voices >= MAX_VOICES && at) { this.dropped++; return; }   // full: others' sounds give way to yours
    const ui = s === 'click' || s === 'open' || s === 'close' || s === 'blip';
    const out = ctx.createGain(); out.gain.value = gain;
    let node: AudioNode = out;
    if (pan) { const p = ctx.createStereoPanner(); p.pan.value = pan; out.connect(p); node = p; }
    node.connect(ui ? this.uiGain : this.sfxGain);
    const len = RECIPES[s](ctx, out, this.noise!, ctx.currentTime + 0.005, pitch);
    this.voices++;
    setTimeout(() => { this.voices--; try { node.disconnect(); out.disconnect(); } catch { /* gone */ } }, (len + 0.1) * 1000);
  }
}

// ---------- effect recipes: build the sound into `out`, return its length in seconds ----------
type Recipe = (c: BaseAudioContext, out: AudioNode, noise: AudioBuffer, t: number, pitch: number) => number;

function tone(c: BaseAudioContext, out: AudioNode, type: OscillatorType, f0: number, f1: number, t: number, dur: number, vol: number, attack = 0.005): void {
  const o = c.createOscillator(), g = c.createGain();
  o.type = type; o.frequency.setValueAtTime(f0, t); if (f1 !== f0) o.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t + dur);
  g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(vol, t + attack); g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  o.connect(g); g.connect(out); o.start(t); o.stop(t + dur + 0.02);
}
function hiss(c: BaseAudioContext, out: AudioNode, noise: AudioBuffer, t: number, dur: number, vol: number, f0: number, f1: number, q = 1.2): void {
  const s = c.createBufferSource(), f = c.createBiquadFilter(), g = c.createGain();
  s.buffer = noise; f.type = 'bandpass'; f.Q.value = q; f.frequency.setValueAtTime(f0, t); f.frequency.exponentialRampToValueAtTime(f1, t + dur);
  g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(vol, t + 0.01); g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  s.connect(f); f.connect(g); g.connect(out); s.start(t, Math.random() * 0.3); s.stop(t + dur + 0.02);
}
const note = (n: number) => 440 * Math.pow(2, (n - 69) / 12);

const RECIPES: Record<Sfx, Recipe> = {
  dash: (c, o, n, t, p) => { hiss(c, o, n, t, 0.16, 0.35, 900 * p, 3200 * p, 0.9); tone(c, o, 'triangle', 300 * p, 520 * p, t, 0.08, 0.06); return 0.18; },
  attack: (c, o, n, t, p) => { hiss(c, o, n, t, 0.12, 0.3, 2600 * p, 700 * p, 1.6); tone(c, o, 'square', 180 * p, 90 * p, t + 0.02, 0.07, 0.05); return 0.15; },
  loot: (c, o, _n, t) => { tone(c, o, 'square', note(76), note(76), t, 0.08, 0.08); tone(c, o, 'square', note(83), note(83), t + 0.07, 0.14, 0.08); return 0.24; },
  // the victory jingle: a quick run up, a held chord, and a little bow at the end
  fanfare: (c, o, _n, t) => {
    const q = 0.11;
    [67, 67, 67, 72].forEach((m, i) => tone(c, o, 'square', note(m), note(m), t + i * q, i === 3 ? 0.5 : 0.09, 0.08));
    [[64, 72, 76], [65, 74, 77], [67, 76, 79]].forEach((ch, i) => ch.forEach(m => tone(c, o, i === 2 ? 'square' : 'triangle', note(m), note(m), t + 0.62 + i * 0.24, i === 2 ? 0.9 : 0.2, i === 2 ? 0.05 : 0.07)));
    tone(c, o, 'triangle', note(48), note(48), t + 0.62, 0.3, 0.14); tone(c, o, 'triangle', note(43), note(43), t + 0.86, 0.3, 0.14); tone(c, o, 'triangle', note(48), note(48), t + 1.1, 0.9, 0.16);
    return 2.1;
  },
  level: (c, o, _n, t) => { [72, 76, 79, 84].forEach((m, i) => tone(c, o, 'square', note(m), note(m), t + i * 0.09, i === 3 ? 0.45 : 0.12, 0.08)); tone(c, o, 'triangle', note(48), note(48), t + 0.27, 0.5, 0.12); return 0.8; },
  click: (c, o, _n, t) => { tone(c, o, 'square', 1250, 900, t, 0.03, 0.04); return 0.05; },
  open: (c, o, _n, t) => { tone(c, o, 'triangle', note(72), note(79), t, 0.09, 0.1); return 0.1; },
  close: (c, o, _n, t) => { tone(c, o, 'triangle', note(79), note(72), t, 0.08, 0.08); return 0.1; },
  invite: (c, o, _n, t) => { [84, 88, 91].forEach((m, i) => tone(c, o, 'sine', note(m), note(m), t + i * 0.11, 0.5, 0.12, 0.002)); return 0.8; },
  join: (c, o, _n, t) => { [67, 72, 76].forEach((m, i) => tone(c, o, 'triangle', note(m), note(m), t + i * 0.07, 0.22, 0.12)); return 0.4; },
  ping: (c, o, _n, t) => { tone(c, o, 'sine', note(86), note(86), t, 0.18, 0.07, 0.002); return 0.2; },
  whisper: (c, o, _n, t) => { tone(c, o, 'sine', note(81), note(81), t, 0.12, 0.06, 0.002); tone(c, o, 'sine', note(88), note(88), t + 0.08, 0.2, 0.06, 0.002); return 0.3; },
  error: (c, o, _n, t) => { tone(c, o, 'square', 140, 110, t, 0.16, 0.06); return 0.18; },
  shutter: (c, o, n, t) => { hiss(c, o, n, t, 0.05, 0.4, 4000, 2500, 0.8); hiss(c, o, n, t + 0.07, 0.06, 0.3, 3000, 1800, 0.8); tone(c, o, 'square', 1800, 1200, t, 0.02, 0.04); return 0.15; },
  // combat: soft and bouncy rather than crunchy
  hit: (c, o, n, t, p) => { tone(c, o, 'triangle', 420 * p, 210 * p, t, 0.08, 0.12); hiss(c, o, n, t, 0.05, 0.15, 1800 * p, 900 * p, 1.2); return 0.1; },
  hurt: (c, o, n, t) => { tone(c, o, 'square', 300, 160, t, 0.12, 0.07); hiss(c, o, n, t, 0.08, 0.2, 1200, 500, 1); return 0.14; },
  poof: (c, o, n, t, p) => { hiss(c, o, n, t, 0.25, 0.25, 600 * p, 3000 * p, 0.7); [79, 84].forEach((m, i) => tone(c, o, 'sine', note(m) * p, note(m) * p, t + 0.05 + i * 0.06, 0.12, 0.06, 0.002)); return 0.3; },
  dodge: (c, o, n, t) => { hiss(c, o, n, t, 0.1, 0.2, 3000, 5000, 1); tone(c, o, 'sine', note(91), note(96), t, 0.08, 0.05, 0.002); return 0.12; },
  // the battle intro: a falling whoosh, then a bright little fanfare climbing up (SNES style)
  battle: (c, o, n, t) => {
    hiss(c, o, n, t, 0.5, 0.35, 5000, 400, 0.6);
    tone(c, o, 'sawtooth', 880, 110, t, 0.45, 0.05);
    [64, 67, 71, 76, 79, 83, 88].forEach((m, i) => tone(c, o, 'square', note(m), note(m), t + 0.42 + i * 0.045, 0.09, 0.07));
    tone(c, o, 'triangle', note(40), note(40), t + 0.42, 0.5, 0.16);
    tone(c, o, 'square', note(88), note(88), t + 0.74, 0.35, 0.06);
    return 1.2;
  },
  chain: (c, o, _n, t, p) => { [72, 76, 79, 84].forEach((m, i) => tone(c, o, 'square', note(m) * p, note(m) * p, t + i * 0.035, 0.08, 0.06)); return 0.25; },
  jump: (c, o, _n, t, p) => { tone(c, o, 'square', 330 * p, 660 * p, t, 0.09, 0.05); return 0.1; },
  mount: (c, o, n, t) => { hiss(c, o, n, t, 0.2, 0.18, 800, 2400, 0.7); [67, 74, 79].forEach((m, i) => tone(c, o, 'triangle', note(m), note(m), t + i * 0.05, 0.14, 0.08)); return 0.3; },
  blip: (c, o, _n, t, p) => { tone(c, o, 'square', 520 * p, 520 * p, t, 0.035, 0.035, 0.002); return 0.05; },
  // spells: each element has its own voice
  cast: (c, o, _n, t) => { [79, 84, 88, 91].forEach((m, i) => tone(c, o, 'sine', note(m), note(m + 12), t + i * 0.04, 0.12, 0.04, 0.002)); return 0.3; },
  fire: (c, o, n, t) => { hiss(c, o, n, t, 0.5, 0.4, 300, 1800, 0.6); hiss(c, o, n, t + 0.12, 0.35, 0.25, 1200, 400, 0.8); tone(c, o, 'sawtooth', 120, 60, t, 0.35, 0.05); return 0.55; },
  ice: (c, o, n, t) => { [96, 91, 98, 93, 100].forEach((m, i) => tone(c, o, 'triangle', note(m), note(m), t + i * 0.05, 0.18, 0.05, 0.002)); hiss(c, o, n, t, 0.3, 0.15, 6000, 3000, 2); return 0.45; },
  thunder: (c, o, n, t) => { hiss(c, o, n, t, 0.06, 0.6, 4000, 2000, 0.5); tone(c, o, 'square', 90, 45, t + 0.03, 0.4, 0.12); hiss(c, o, n, t + 0.08, 0.5, 0.3, 300, 120, 0.4); return 0.6; },
  holy: (c, o, _n, t) => { [72, 79, 84, 88, 91].forEach((m, i) => tone(c, o, 'sine', note(m), note(m), t + i * 0.06, 0.5, 0.05, 0.01)); tone(c, o, 'triangle', note(96), note(96), t + 0.3, 0.5, 0.04); return 0.8; },
  heal: (c, o, _n, t) => { [76, 79, 83, 88].forEach((m, i) => tone(c, o, 'sine', note(m), note(m), t + i * 0.07, 0.35, 0.06, 0.01)); return 0.6; },
  buff: (c, o, _n, t) => { tone(c, o, 'triangle', note(67), note(79), t, 0.25, 0.08); tone(c, o, 'sine', note(84), note(84), t + 0.18, 0.25, 0.05); return 0.45; },
  debuff: (c, o, _n, t) => { tone(c, o, 'triangle', note(72), note(60), t, 0.3, 0.08); tone(c, o, 'sine', note(66), note(54), t + 0.12, 0.3, 0.05); return 0.45; },
  poison: (c, o, _n, t) => { [0, 1, 2].forEach(i => tone(c, o, 'sine', 300 + i * 70, 500 + i * 90, t + i * 0.08, 0.07, 0.05, 0.002)); return 0.3; },
  steal: (c, o, n, t) => { hiss(c, o, n, t, 0.08, 0.2, 3500, 6000, 1.2); tone(c, o, 'square', note(88), note(93), t + 0.06, 0.08, 0.05); return 0.18; },
  burst: (c, o, n, t) => { hiss(c, o, n, t, 0.4, 0.4, 600, 5000, 0.5); [60, 67, 72, 79, 84].forEach((m, i) => tone(c, o, 'square', note(m), note(m), t + 0.05 + i * 0.03, 0.3, 0.06)); tone(c, o, 'sawtooth', 220, 55, t, 0.5, 0.06); return 0.6; },
};

// ---------- music ----------
export type SongId = 'town' | 'battle' | 'boss';
interface Song {
  bpm: number;
  /** One chord per bar (MIDI notes), eight bars. */
  chords: number[][];
  /** 64 eighth notes; -1 is a rest. */
  melody: number[];
  /** How it moves: the cozy walk of the town tune, or a driving fight. */
  style: 'cozy' | 'drive';
  lead: OscillatorType;
}
const SONGS: Record<SongId, Song> = {
  // I - vi - IV - V - I - iii - IV - V in C
  town: { bpm: 88, style: 'cozy', lead: 'square', chords: [[60, 64, 67], [57, 60, 64], [53, 57, 60], [55, 59, 62], [60, 64, 67], [52, 55, 59], [53, 57, 60], [55, 59, 62]],
    melody: [76, -1, 74, 72, 74, -1, 76, 79, 72, -1, -1, 69, 72, 74, 72, -1, 69, -1, 72, 74, 76, -1, 74, 72, 74, -1, -1, 71, 74, -1, 79, -1,
      76, -1, 79, 81, 79, -1, 76, 74, 76, -1, 72, -1, 71, 72, 74, -1, 72, -1, 69, 72, 69, -1, 67, 69, 67, -1, 71, 74, 72, -1, -1, -1] },
  // a fight: A minor, i - VI - VII - v, then i - VI - VII - V for the lift back round; a running bass and drums
  battle: { bpm: 152, style: 'drive', lead: 'square', chords: [[57, 60, 64], [53, 57, 60], [55, 59, 62], [52, 55, 59], [57, 60, 64], [53, 57, 60], [55, 59, 62], [52, 56, 59]],
    melody: [76, -1, 76, 77, 79, -1, 77, 76, 72, -1, 72, 74, 76, -1, 72, -1, 74, -1, 74, 76, 77, -1, 76, 74, 71, -1, 71, 72, 74, -1, 79, -1,
      81, -1, 79, 77, 76, -1, 72, 76, 77, -1, 76, 74, 72, -1, 69, -1, 71, 72, 74, -1, 77, 76, 74, 72, 68, -1, 71, -1, 76, -1, 75, 76] },
  // a boss: D minor, slower and heavier, with a low tom
  boss: { bpm: 138, style: 'drive', lead: 'sawtooth', chords: [[50, 53, 57], [46, 50, 53], [48, 52, 55], [45, 49, 52], [50, 53, 57], [46, 50, 53], [43, 46, 50], [45, 49, 52]],
    melody: [74, -1, -1, 72, 74, -1, 77, -1, 70, -1, -1, 69, 70, -1, 74, -1, 72, -1, -1, 70, 72, -1, 76, -1, 73, -1, 76, -1, 79, -1, 76, 73,
      74, -1, 77, -1, 81, -1, 79, 77, 74, -1, 70, -1, 74, -1, 77, -1, 79, -1, 77, 74, 70, -1, 67, -1, 69, -1, 73, -1, 76, -1, 73, -1] },
};

/** The looping music: the cozy town tune, or a fight song. Changing song waits for the next beat and starts the
 *  new one from its first bar, with a short dip so the switch doesn't clash. */
export class Music {
  private timer = 0; private step = 0; private next = 0; private on = false;
  private song: Song = SONGS.town; private want: Song | null = null;
  private shaker: AudioBuffer; private noise: AudioBuffer;
  constructor(private c: BaseAudioContext, private out: AudioNode) {
    const mk = (sec: number) => { const len = Math.floor(c.sampleRate * sec), b = c.createBuffer(1, len, c.sampleRate), d = b.getChannelData(0); for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / len); return b; };
    this.shaker = mk(0.03); this.noise = mk(0.18);
  }
  get current(): SongId { return (Object.keys(SONGS) as SongId[]).find(k => SONGS[k] === (this.want ?? this.song))!; }

  setSong(id: SongId): void { const s = SONGS[id]; if (s === this.song && !this.want) return; if (!this.on) { this.song = s; this.step = 0; return; } this.want = s; }

  play(): void {
    if (this.on || !(this.c instanceof AudioContext)) return;
    const c = this.c;
    this.on = true; this.next = c.currentTime + 0.1;
    const look = () => {
      // schedule a little ahead of time so timer jitter never makes the music stutter
      while (this.next < c.currentTime + 0.25) {
        if (this.want && this.step % 2 === 0) { this.song = this.want; this.want = null; this.step = 0; this.next += 0.12; }
        this.schedule(this.step, this.next); this.next += 60 / this.song.bpm / 2; this.step = (this.step + 1) % 64;
      }
    };
    look(); this.timer = window.setInterval(look, 60);
  }
  stop(): void { this.on = false; clearInterval(this.timer); }

  schedule(i: number, t: number, song = this.song): void {
    const bar = Math.floor(i / 8), beat = i % 8, ch = song.chords[bar], c = this.c, o = this.out;
    const n = (m: number) => 440 * Math.pow(2, (m - 69) / 12);
    const noise = (buf: AudioBuffer, type: BiquadFilterType, freq: number, vol: number) => { const sh = c.createBufferSource(), f = c.createBiquadFilter(), g = c.createGain(); sh.buffer = buf; f.type = type; f.frequency.value = freq; g.gain.value = vol; sh.connect(f); f.connect(g); g.connect(o); sh.start(t); };
    const m = song.melody[i];
    if (song.style === 'cozy') {
      if (beat % 4 === 0) tone(c, o, 'triangle', n(ch[0] - 24), n(ch[0] - 24), t, 0.6, 0.16, 0.01);         // bass on 1 and 3
      if (beat % 4 === 2) tone(c, o, 'triangle', n(ch[2] - 24), n(ch[2] - 24), t, 0.3, 0.1, 0.01);
      tone(c, o, 'sine', n(ch[beat % 3] + 12), n(ch[beat % 3] + 12), t, 0.28, 0.035, 0.01);                // arpeggio
      if (beat === 0) for (const x of ch) tone(c, o, 'triangle', n(x), n(x), t, 1.6, 0.022, 0.25);           // soft pad
      if (m > 0) tone(c, o, song.lead, n(m), n(m), t, 0.32, 0.028, 0.01);
      if (beat % 2 === 1) noise(this.shaker, 'highpass', 6000, 0.025);                                     // a tiny shaker on the off-beats
      return;
    }
    // a fight: eighth-note bass that jumps the octave, chord stabs on the off-beats, a staccato lead and drums
    const root = ch[0] - (ch[0] >= 55 ? 24 : 12);
    tone(c, o, 'triangle', n(root + (beat % 2 ? 12 : 0)), n(root + (beat % 2 ? 12 : 0)), t, 0.16, 0.15, 0.004);
    tone(c, o, 'square', n(root), n(root), t, 0.12, 0.018, 0.004);
    if (beat === 3 || beat === 6 || beat === 7) for (const x of ch) tone(c, o, 'triangle', n(x), n(x), t, 0.12, 0.03, 0.004);
    if (beat === 0) for (const x of ch) tone(c, o, 'sawtooth', n(x - 12), n(x - 12), t, 1.4, 0.008, 0.08);   // a low swell under each bar
    if (m > 0) { tone(c, o, song.lead, n(m), n(m), t, 0.17, song.lead === 'sawtooth' ? 0.02 : 0.03, 0.005); tone(c, o, 'sine', n(m + 12), n(m + 12), t, 0.1, 0.01, 0.005); }
    if (beat === 0 || beat === 4 || (beat === 7 && bar % 2)) tone(c, o, 'sine', 150, 42, t, 0.16, 0.32, 0.002);   // kick
    if (beat === 2 || beat === 6) { noise(this.noise, 'highpass', 1400, 0.12); tone(c, o, 'triangle', 220, 160, t, 0.07, 0.06, 0.002); }   // snare
    noise(this.shaker, 'highpass', 7000, beat % 2 ? 0.03 : 0.018);                                         // hats
    if (song === SONGS.boss && (beat === 5 || beat === 7)) tone(c, o, 'sine', 110, 70, t, 0.2, 0.16, 0.003);   // low tom
    if (bar === 7 && beat >= 4) noise(this.noise, 'bandpass', 900 + beat * 300, 0.05);                       // a fill into the top
  }
}

/** Renders the music and every effect to a buffer without playing it (for listening tests and tools). */
export async function renderPreview(seconds = 24): Promise<AudioBuffer> {
  const rate = 44100, c = new OfflineAudioContext(2, rate * (seconds * 2 + 20), rate), out = c.createGain(); out.gain.value = 0.6; out.connect(c.destination);
  const m = new Music(c, out), step = 60 / 88 / 2;
  for (let i = 0, t = 0.1; t < seconds; i++, t += step) m.schedule(i % 64, t);
  const fight = SONGS.battle, fs = 60 / fight.bpm / 2;   // then the fight song
  for (let i = 0, t = seconds + 0.1; t < seconds * 2; i++, t += fs) m.schedule(i % 64, t, fight);
  const n = c.createBuffer(1, rate / 2, rate), d = n.getChannelData(0); for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  (Object.keys(RECIPES) as Sfx[]).forEach((k, i) => RECIPES[k](c, out, n, seconds * 2 + 0.1 + i * 0.42, 1));
  return c.startRendering();
}
